import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CheckIcon,
  CloudArrowUpIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  PlayIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import DashboardLayout from "../../components/DashboardLayout";
import MediaPaywall from "../../components/account/MediaPaywall";
import WeaveLoader from "../../components/common/WeaveLoader";
import MediaDetailDrawer from "../../components/media/MediaDetailDrawer";
import UploadQueue from "../../components/media/UploadQueue";
import { useToast } from "../../contexts/ToastContext";
import { mediaService } from "../../services/media";
import { getApiErrorMessage } from "../../utils/axios";
import { useConfirmLeave } from "../../utils/useConfirmLeave";
import { sitePath } from "../../utils/urls";

const ALLOWED_EXT = new Set([
  ".png", ".jpg", ".jpeg", ".webp", ".gif", ".svg", ".ico", ".mov", ".mp4",
]);
const MAX_BYTES = 50 * 1024 * 1024;
const CONCURRENCY = 3;
const SEARCH_DEBOUNCE_MS = 200;

let uploadSeq = 0;

const extOf = (name) => {
  const i = (name || "").lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
};

const Media = ({ currentUser }) => {
  const { error: toastError, success: toastSuccess } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { siteSlug, "*": rest } = useParams();
  const pathParts = (rest || "").split("/").filter(Boolean);
  const mediaHash = pathParts[0] || null;
  const project = useSelector((state) => state.project.currentProject);
  const hasMediaAccess = Boolean(currentUser?.features?.media);

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");
  const agentRun = searchParams.get("agent_run") || "";
  const changeRequest = searchParams.get("change_request") || "";
  const [selected, setSelected] = useState(() => new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [queue, setQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const fileInputRef = useRef(null);
  const abortMap = useRef(new Map());
  const queueRef = useRef([]);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) next.set("search", debouncedSearch);
    else next.delete("search");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const listParams = useMemo(
    () => ({
      search: debouncedSearch,
      agent_run: agentRun,
      change_request: changeRequest,
    }),
    [debouncedSearch, agentRun, changeRequest],
  );

  const loadPage = useCallback(
    (pageNum, { append } = {}) => {
      if (!hasMediaAccess || !project?.hash) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      mediaService.list(
        project.hash,
        { ...listParams, page: pageNum },
        (response) => {
          const data = response.data;
          const list = data?.results || (Array.isArray(data) ? data : []);
          setItems((prev) => (append ? [...prev, ...list] : list));
          setHasMore(Boolean(data?.next));
          setTotalCount(
            typeof data?.count === "number" ? data.count : list.length,
          );
          setPage(pageNum);
          setLoading(false);
          setLoadingMore(false);
          if (!append) setSelected(new Set());
        },
        (err) => {
          setLoading(false);
          setLoadingMore(false);
          toastError(getApiErrorMessage(err, "Could not load media."));
        },
      );
    },
    [hasMediaAccess, project?.hash, listParams, toastError],
  );

  useEffect(() => {
    if (!hasMediaAccess) return;
    loadPage(1);
  }, [hasMediaAccess, loadPage]);

  useEffect(() => {
    if (!hasMediaAccess || !mediaHash || !project?.hash) {
      setDetailItem(null);
      return;
    }
    const cached = items.find((i) => i.hash === mediaHash);
    if (cached?.versions) {
      setDetailItem(cached);
      return;
    }
    mediaService.get(
      project.hash,
      mediaHash,
      (response) => setDetailItem(response.data),
      () => setDetailItem(cached || null),
    );
  }, [hasMediaAccess, mediaHash, project?.hash, items]);

  const uploadingCount = queue.filter(
    (q) => q.status === "uploading" || q.status === "queued",
  ).length;

  const pumpQueueRef = useRef(() => {});

  const startUpload = useCallback((item) => {
    if (!project?.hash) return;
    const controller = new AbortController();
    abortMap.current.set(item.id, controller);
    setQueue((q) =>
      q.map((x) => (x.id === item.id ? { ...x, status: "uploading", progress: 0 } : x)),
    );
    mediaService.upload(
      project.hash,
      item.file,
      {
        signal: controller.signal,
        onUploadProgress: (event) => {
          const total = event.total || item.file.size || 1;
          const progress = Math.min(100, Math.round((event.loaded / total) * 100));
          setQueue((q) =>
            q.map((x) => (x.id === item.id ? { ...x, progress } : x)),
          );
        },
      },
      (response) => {
        abortMap.current.delete(item.id);
        setQueue((q) =>
          q.map((x) => (x.id === item.id ? { ...x, status: "done", progress: 100 } : x)),
        );
        setItems((prev) => [response.data, ...prev]);
        setTotalCount((n) => n + 1);
        window.setTimeout(() => pumpQueueRef.current(), 0);
      },
      (err) => {
        abortMap.current.delete(item.id);
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
          setQueue((q) => q.filter((x) => x.id !== item.id));
        } else {
          setQueue((q) =>
            q.map((x) =>
              x.id === item.id
                ? { ...x, status: "failed", error: getApiErrorMessage(err, "Upload failed") }
                : x,
            ),
          );
        }
        window.setTimeout(() => pumpQueueRef.current(), 0);
      },
    );
  }, [project?.hash]);

  const pumpQueue = useCallback(() => {
    if (!project?.hash) return;
    const current = queueRef.current;
    const uploading = current.filter((q) => q.status === "uploading").length;
    const slots = CONCURRENCY - uploading;
    if (slots <= 0) return;
    current
      .filter((q) => q.status === "queued")
      .slice(0, slots)
      .forEach((item) => startUpload(item));
  }, [project?.hash, startUpload]);

  useEffect(() => {
    pumpQueueRef.current = pumpQueue;
  }, [pumpQueue]);

  const cancelRemaining = useCallback(() => {
    abortMap.current.forEach((controller) => controller.abort());
    abortMap.current.clear();
    setQueue((q) => q.filter((x) => x.status === "done"));
  }, []);

  useConfirmLeave({
    active: uploadingCount > 0 && Boolean(project),
    basePath: project ? sitePath(project, "media/").replace(/\/$/, "") : "",
    message: `You have ${uploadingCount} upload${uploadingCount === 1 ? "" : "s"} in progress. Leave and cancel them?`,
    onLeave: cancelRemaining,
  });

  const enqueueFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const newItems = files.map((file) => {
      const id = `u-${++uploadSeq}`;
      const ext = extOf(file.name);
      let status = "queued";
      let error = "";
      if (!ALLOWED_EXT.has(ext)) {
        status = "rejected";
        error = `Unsupported type ${ext || "(none)"}`;
      } else if (file.size > MAX_BYTES) {
        status = "rejected";
        error = "File too large (max 50 MB)";
      }
      const previewUrl =
        status !== "rejected" && file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : "";
      return {
        id,
        file,
        name: file.name,
        status,
        progress: 0,
        error,
        previewUrl,
      };
    });
    setQueue((q) => [...q, ...newItems]);
    window.setTimeout(() => pumpQueueRef.current(), 0);
  };

  const onRetry = (id) => {
    setQueue((q) =>
      q.map((x) =>
        x.id === id ? { ...x, status: "queued", error: "", progress: 0 } : x,
      ),
    );
    window.setTimeout(() => pumpQueueRef.current(), 0);
  };

  const closeQueue = () => {
    queue.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
    setQueue([]);
  };

  const toggleSelect = (hash) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(hash)) next.delete(hash);
      else next.add(hash);
      return next;
    });
  };

  const pageHashes = useMemo(() => items.map((i) => i.hash), [items]);
  const allPageSelected = pageHashes.length > 0 && pageHashes.every((h) => selected.has(h));
  const somePageSelected = pageHashes.some((h) => selected.has(h));

  const toggleSelectPage = () => {
    setSelected((prev) => {
      if (allPageSelected) {
        const next = new Set(prev);
        pageHashes.forEach((h) => next.delete(h));
        return next;
      }
      const next = new Set(prev);
      pageHashes.forEach((h) => next.add(h));
      return next;
    });
  };

  const clearRunFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("agent_run");
    next.delete("change_request");
    setSearchParams(next, { replace: true });
  };

  const confirmDelete = () => {
    if (!project?.hash || !selected.size) return;
    setDeleting(true);
    mediaService.bulkDelete(
      project.hash,
      Array.from(selected),
      () => {
        const removed = selected.size;
        setDeleting(false);
        setDeleteConfirm(false);
        setItems((prev) => prev.filter((i) => !selected.has(i.hash)));
        setTotalCount((n) => Math.max(0, n - removed));
        setSelected(new Set());
        toastSuccess("Media deleted");
        if (mediaHash && selected.has(mediaHash)) {
          navigate(sitePath(siteSlug, "media/"));
        }
      },
      (err) => {
        setDeleting(false);
        toastError(getApiErrorMessage(err, "Could not delete media."));
      },
    );
  };

  const uploadButton = (
    <>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="inline-flex items-center gap-1 rounded-lg bg-theme-primary px-3 py-1.5 text-xs font-semibold text-theme-button lg:px-4 lg:py-2 lg:text-sm"
      >
        <CloudArrowUpIcon className="h-4 w-4" />
        Upload
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".png,.jpg,.jpeg,.webp,.gif,.svg,.ico,.mov,.mp4"
        className="hidden"
        onChange={(e) => {
          enqueueFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );

  if (!hasMediaAccess) {
    return (
      <DashboardLayout title="Media" currentUser={currentUser}>
        <MediaPaywall />
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout title="Media" currentUser={currentUser}>
        <div className="flex justify-center py-20">
          <WeaveLoader size={48} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Media"
      currentUser={currentUser}
      primaryAction={uploadButton}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={toggleSelectPage}
            disabled={!pageHashes.length}
            title={allPageSelected ? "Deselect page" : "Select page"}
            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
              allPageSelected || somePageSelected
                ? "border-theme-primary bg-theme-primary text-theme-button"
                : "border-theme-secondary text-theme-text opacity-50 hover:opacity-100"
            } disabled:opacity-30`}
          >
            {(allPageSelected || somePageSelected) && (
              <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            <span className="sr-only">
              {allPageSelected ? "Deselect all on page" : "Select all on page"}
            </span>
          </button>
          <div className="flex min-w-0 flex-wrap items-baseline gap-2">
            <h2 className="text-base font-semibold text-theme-text">Images</h2>
            <span className="text-sm text-theme-secondary-text">({totalCount})</span>
          </div>
          {selected.size > 0 && (
            <button
              type="button"
              onClick={() => setDeleteConfirm(true)}
              title={`Delete ${selected.size} selected`}
              className="inline-flex shrink-0 rounded p-1 text-theme-text opacity-50 hover:opacity-100"
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">Delete selected</span>
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-theme-text opacity-40" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search"
              className="w-40 rounded-lg border border-theme-secondary bg-theme-main py-1.5 pl-8 pr-2.5 text-sm text-theme-text sm:w-48"
            />
          </label>
          <div className="inline-flex rounded-lg border border-theme-secondary p-0.5">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`rounded-md p-1.5 ${viewMode === "grid" ? "bg-theme-secondary" : "opacity-50 hover:opacity-100"}`}
              title="Grid view"
            >
              <Squares2X2Icon className="h-4 w-4 text-theme-text" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`rounded-md p-1.5 ${viewMode === "list" ? "bg-theme-secondary" : "opacity-50 hover:opacity-100"}`}
              title="List view"
            >
              <ListBulletIcon className="h-4 w-4 text-theme-text" />
            </button>
          </div>
        </div>
      </div>

        {(agentRun || changeRequest) && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-theme-text">
            <span className="rounded bg-theme-secondary px-2 py-1 opacity-80">
              Filtered by {agentRun ? `agent run ${agentRun.slice(0, 8)}…` : `change ${changeRequest.slice(0, 8)}…`}
            </span>
            <button type="button" onClick={clearRunFilters} className="underline opacity-60 hover:opacity-100">
              Clear filter
            </button>
          </div>
        )}

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            enqueueFiles(e.dataTransfer.files);
          }}
          className={`min-h-[320px] rounded-xl border-2 border-dashed p-4 transition-colors ${
            dragOver
              ? "border-theme-primary bg-theme-primary bg-opacity-5"
              : "border-theme-secondary"
          }`}
        >
          {loading ? (
            <div className="flex justify-center py-20">
              <WeaveLoader size={48} />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-theme-text opacity-60">
              <CloudArrowUpIcon className="mb-2 h-10 w-10" />
              <p className="text-sm">Drop files here or click Upload</p>
              <p className="text-xs">png, jpg, webp, gif, svg, ico, mov, mp4</p>
            </div>
          ) : viewMode === "list" ? (
            <div className="divide-y divide-theme-secondary">
              {items.map((item) => {
                const isVideo = (item.content_type || "").startsWith("video/");
                const isSelected = selected.has(item.hash);
                return (
                  <div key={item.hash} className="flex items-center gap-3 py-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(item.hash)}
                      className="h-3.5 w-3.5"
                    />
                    <button
                      type="button"
                      onClick={() => navigate(sitePath(siteSlug, `media/${item.hash}/`))}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left hover:opacity-80"
                    >
                      <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-theme-secondary">
                        {isVideo ? (
                          <div className="relative flex h-full w-full items-center justify-center">
                            <PlayIcon className="h-5 w-5 text-theme-text opacity-60" />
                          </div>
                        ) : (
                          <MediaThumb url={item.url} alt="" mediaType={item.media_type} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-theme-text">
                          {item.title || item.hash}
                        </p>
                        <p className="truncate text-xs text-theme-text opacity-50">
                          {[item.media_type, item.content_type, item.width && item.height ? `${item.width}×${item.height}` : ""]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {items.map((item) => {
                const isVideo = (item.content_type || "").startsWith("video/");
                const isSelected = selected.has(item.hash);
                return (
                  <div
                    key={item.hash}
                    className={`group relative overflow-hidden rounded-lg border bg-theme-secondary ${
                      isSelected ? "border-theme-primary" : "border-theme-secondary"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => navigate(sitePath(siteSlug, `media/${item.hash}/`))}
                      className="block aspect-square w-full overflow-hidden"
                    >
                      {isVideo ? (
                        <div className="relative flex h-full w-full items-center justify-center">
                          <video
                            src={item.url}
                            muted
                            className="h-full w-full object-cover"
                          />
                          <PlayIcon className="absolute h-8 w-8 text-white drop-shadow" />
                        </div>
                      ) : (
                        <MediaThumb
                          url={item.url}
                          alt={item.title || ""}
                          mediaType={item.media_type}
                        />
                      )}
                    </button>
                    <label className="absolute left-2 top-2 z-20 flex h-5 w-5 cursor-pointer items-center justify-center rounded border border-theme-secondary bg-theme-main">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(item.hash)}
                        className="h-3 w-3"
                      />
                    </label>
                    <p className="truncate px-2 py-1.5 text-xs text-theme-text">
                      {item.title || item.hash}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => loadPage(page + 1, { append: true })}
                className="rounded-lg border border-theme-secondary px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-5 disabled:opacity-50"
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>

      {mediaHash && detailItem && (
        <MediaDetailDrawer
          project={project}
          item={detailItem}
          showCost={Boolean(currentUser?.features?.build_cost)}
          onSaved={(updated) => {
            setDetailItem(updated);
            setItems((prev) =>
              prev.map((i) => (i.hash === updated.hash ? { ...i, ...updated } : i)),
            );
          }}
        />
      )}

      <UploadQueue
        items={queue}
        onRetry={onRetry}
        onCancelRemaining={cancelRemaining}
        onClose={closeQueue}
      />

      {deleteConfirm &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <button
              type="button"
              className="absolute inset-0 bg-theme-text opacity-40"
              aria-label="Close"
              onClick={() => setDeleteConfirm(false)}
            />
            <div className="relative w-full max-w-md rounded-xl border border-theme-secondary bg-theme-main p-5 shadow-xl">
              <h3 className="text-lg font-semibold text-theme-text">
                Delete {selected.size} media item{selected.size === 1 ? "" : "s"}?
              </h3>
              <p className="mt-2 text-sm text-theme-text opacity-70">
                This cannot be undone. Media already used on a site will stop showing there.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(false)}
                  className="rounded-lg border border-theme-secondary px-4 py-2 text-sm text-theme-text"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={confirmDelete}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </DashboardLayout>
  );
};

function MediaThumb({ url, alt, mediaType }) {
  // Favicons are tiny squares; don't upscale them to fill the cell (looks stretched).
  const isFavicon = mediaType === "favicon";
  const isVideo =
    mediaType === "meta_video" ||
    /\.(mp4|mov|webm)(\?|$)/i.test(url || "");
  if (isVideo) {
    return (
      <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-theme-secondary">
        <video
          src={url}
          muted
          loop
          playsInline
          autoPlay
          className="relative z-10 h-full w-full object-contain"
        />
      </div>
    );
  }
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
      <img
        src={url}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full scale-125 object-cover blur-2xl"
      />
      <img
        src={url}
        alt={alt}
        className={
          isFavicon
            ? "relative z-10 h-1/2 w-1/2 object-contain"
            : "relative z-10 h-full w-full object-contain"
        }
      />
    </div>
  );
}

export default Media;
